import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa6';

import MarkdownContent from '../common/MarkdownContent';
import RichDocContent from '../common/RichDocContent';
import { assetSrc, type HeroSliderBlock } from '../../api/staticPageBlocks';
import './HeroSliderBlockView.scss';

interface HeroSliderBlockViewProps {
  block: HeroSliderBlock;
  assetUrls?: Record<string, string>;
}

const HeroSliderBlockView = ({ block, assetUrls }: HeroSliderBlockViewProps) => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const { slides } = block;
  const hasNav = slides.length > 1;

  const goPrev = () => setIndex((i) => (i - 1 + slides.length) % slides.length);
  const goNext = () => setIndex((i) => (i + 1) % slides.length);

  return (
    <div className="hero-slider">
      {slides.map((slide, i) => {
        const src = assetSrc(slide.asset_ref, assetUrls);
        return (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className={`hero-slider__slide${i === index ? ' is-active' : ''}`}
            aria-hidden={i !== index}
          >
            {src ? (
              <img src={src} alt="" />
            ) : (
              // Deleted/expired asset ref: an empty src makes React complain
              // and the browser refetch the page, so show a placeholder
              // instead (same reasoning as HeroSliderBlockForm's admin thumb).
              <span className="hero-slider__slide-empty" data-testid="hero-slide-empty" />
            )}
            {(slide.caption || slide.caption_md) && (
              <div className={`hero-caption hero-caption--${slide.caption_position ?? 'center'}`}>
                {slide.caption ? (
                  <RichDocContent doc={slide.caption} assetUrls={assetUrls} />
                ) : (
                  <MarkdownContent content={slide.caption_md} />
                )}
              </div>
            )}
          </div>
        );
      })}
      {hasNav && (
        <>
          <button
            type="button"
            className="hero-slider__nav hero-slider__nav--prev"
            aria-label={t('blocks.hero_prev')}
            onClick={goPrev}
          >
            <FaChevronLeft />
          </button>
          <button
            type="button"
            className="hero-slider__nav hero-slider__nav--next"
            aria-label={t('blocks.hero_next')}
            onClick={goNext}
          >
            <FaChevronRight />
          </button>
          <div className="hero-slider__dots">
            {slides.map((_, i) => (
              <button
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                type="button"
                className={`hero-slider__dot${i === index ? ' is-active' : ''}`}
                aria-label={t('blocks.hero_goto', { n: i + 1 })}
                aria-current={i === index ? 'true' : undefined}
                onClick={() => setIndex(i)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default HeroSliderBlockView;
